import { z } from "zod";
import type { Args, Operation } from "../types";
import { scoped } from "../types";
import {
  idNumericParams,
  paginatedSchema,
  paginationQuery,
  type PaginatedResult,
} from "./_common";

export const applicationStatus = z.enum([
  "active",
  "hired",
  "rejected",
  "withdrawn",
]);
export type ApplicationStatus = z.infer<typeof applicationStatus>;

export const applicationSchema = z.object({
  id: z.number(),
  // Synced object id — already returned by the `SELECT *` reads; surfaced here
  // (additive, optional) so a kanban card can open the scope-free object page at
  // /o/<uid> instead of a section-deep route that 404s at the workspace tier.
  uid: z.string().optional(),
  workspace_id: z.number(),
  job_id: z.number(),
  person_id: z.number(),
  pipeline_id: z.number(),
  current_stage_id: z.number(),
  source: z.string().nullable(),
  source_detail: z.string().nullable(),
  applied_at: z.string(),
  status: applicationStatus,
  owner_actor_id: z.number().nullable(),
  rejection_reason: z.string().nullable(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ApplicationShape = z.infer<typeof applicationSchema>;

export const applicationWithJoinsSchema = applicationSchema.extend({
  job_title: z.string(),
  job_slug: z.string(),
  person_display_name: z.string(),
  primary_email: z.string().nullable(),
  current_stage_name: z.string(),
  current_stage_slug: z.string(),
  current_stage_kind: z.string(),
  days_in_stage: z.number(),
  latest_scorecard_rating: z.string().nullable(),
});
export type ApplicationWithJoins = z.infer<typeof applicationWithJoinsSchema>;

export const applicationsListQuery = paginationQuery.extend({
  job_id: z.coerce.number().int().optional(),
  person_id: z.coerce.number().int().optional(),
  stage_kind: z
    .enum(["lead", "active", "offer", "hired", "rejected"])
    .optional(),
  status: applicationStatus.optional(),
});

export const applicationsListResponse = paginatedSchema(
  applicationWithJoinsSchema,
);
export type ApplicationsListResponse = PaginatedResult<ApplicationWithJoins>;

export const applicationsList: Operation<
  void,
  z.infer<typeof applicationsListQuery>,
  void,
  z.infer<typeof applicationsListResponse>
> = {
  method: "GET",
  path: () => "/hiring/api/applications",
  query: applicationsListQuery,
  response: applicationsListResponse,
};


export const stageCountsQuery = z.object({ job_id: z.coerce.number().int() });

export const stageCountSchema = z.object({
  stage_id: z.number(),
  count: z.number(),
});
export type StageCount = z.infer<typeof stageCountSchema>;

/**
 * Per-stage application counts for ONE job — added by RO-3-07.
 *
 * Wraps `countApplicationsByStage()` (a `GROUP BY current_stage_id`), which the
 * server-rendered job page called inline. `applicationsList` is NOT a substitute:
 * it is paginated (`page_size` caps at 200) and carries no per-stage aggregate, so
 * counting its rows client-side silently undercounts any job with more than a
 * page of applicants — wrong numbers that look right, which is worse than none.
 *
 * ── Why this is NOT under `/hiring/api/jobs/...` ──
 *
 * `PUBLIC_PREFIXES` matches `/hiring/api/jobs` by `startsWith`
 * (packages/core/src/auth/route-gate.ts:27) and, on this tree, WITHOUT regard to
 * method — the gate has no method awareness here. Reads are additionally not
 * capability-gated at all: `authorizeRequest` short-circuits on GET/HEAD before
 * the descriptor lookup. So ANY read mounted under that prefix is anonymously
 * readable, whatever its descriptor says.
 *
 * Pipeline stage names and candidate volumes are operator data — the public
 * careers branch of the original page never even computed them. Hanging them off
 * `/hiring/api/jobs/:id/...` for convenience would therefore have published them,
 * with no line of code looking wrong. `/hiring/api/applications` is not a public
 * prefix, so this read requires a session once a credential exists, and needs no
 * prefix widened to do it.
 */
export const applicationsStageCounts: Operation<
  void,
  z.infer<typeof stageCountsQuery>,
  void,
  StageCount[]
> = {
  method: "GET",
  path: () => "/hiring/api/applications/stage-counts",
  query: stageCountsQuery,
  response: z.array(stageCountSchema),
};

export const applicationCreateBody = z.object({
  job_id: z.number().int(),
  person_id: z.number().int().optional(),
  person_create: z
    .object({
      display_name: z.string().min(1),
      email: z.string().email(),
      phone: z.string().optional(),
      linkedin_url: z.string().optional(),
    })
    .optional(),
  source: z.string().optional(),
  source_detail: z.string().optional(),
});

export const applicationsCreate: Operation<
  void,
  void,
  z.infer<typeof applicationCreateBody>,
  ApplicationShape
> = {
  method: "POST",
  path: () => "/hiring/api/applications",
  body: applicationCreateBody,
  response: applicationSchema,
  // new row in the list; stage-counts for the job bump (job_id on body).
  invalidates: [
    applicationsList,
    applicationsStageCounts, // query family — wildcard all job_id filters
  ],
};

export const applicationsGet: Operation<
  { id: number },
  void,
  void,
  ApplicationWithJoins
> = {
  method: "GET",
  path: ({ id }) => `/hiring/api/applications/${id}`,
  params: idNumericParams,
  response: applicationWithJoinsSchema,
};

const scopedAppGet = scoped(
  applicationsGet,
  (args: Args<{ id: number }, void, unknown>) => ({ id: args.params.id }),
);

export const advanceBody = z.object({
  to_stage_id: z.number().int().optional(),
  to_stage_slug: z.string().optional(),
  note: z.string().optional(),
});

export const applicationsAdvance: Operation<
  { id: number },
  void,
  z.infer<typeof advanceBody>,
  ApplicationShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/applications/${id}/advance`,
  params: idNumericParams,
  body: advanceBody,
  response: applicationSchema,
  // stage/status columns on list + detail; stage-counts redistribute.
  invalidates: [applicationsList, scopedAppGet, applicationsStageCounts],
};

export const rejectBody = z.object({ reason: z.string().min(1) });

export const applicationsReject: Operation<
  { id: number },
  void,
  z.infer<typeof rejectBody>,
  ApplicationShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/applications/${id}/reject`,
  params: idNumericParams,
  body: rejectBody,
  response: applicationSchema,
  // status → rejected; list filters + detail + stage-counts all move.
  invalidates: [applicationsList, scopedAppGet, applicationsStageCounts],
};

export const withdrawBody = z.object({ reason: z.string().optional() });

export const applicationsWithdraw: Operation<
  { id: number },
  void,
  z.infer<typeof withdrawBody>,
  ApplicationShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/applications/${id}/withdraw`,
  params: idNumericParams,
  body: withdrawBody,
  response: applicationSchema,
  // status → withdrawn; same surfaces as reject.
  invalidates: [applicationsList, scopedAppGet, applicationsStageCounts],
};

export const noteAddBody = z.object({ body: z.string().min(1) });
export const noteShape = z.object({
  id: z.number(),
  body: z.string(),
  author: z.string().nullable(),
  created_at: z.string(),
});
export type NoteShape = z.infer<typeof noteShape>;

// Notes are not on applicationsGet / applicationsList payloads (no notes
// Operation exists). updated_at on the application row does bump, which both
// list (ORDER BY updated_at) and get expose — declare those so the board
// reorders and detail refreshes after a note.
export const applicationsAddNote: Operation<
  { id: number },
  void,
  z.infer<typeof noteAddBody>,
  NoteShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/applications/${id}/notes`,
  params: idNumericParams,
  body: noteAddBody,
  response: noteShape,
  invalidates: [applicationsList, scopedAppGet],
};

export const hiringApplications = {
  list: applicationsList,
  stageCounts: applicationsStageCounts,
  create: applicationsCreate,
  get: applicationsGet,
  advance: applicationsAdvance,
  reject: applicationsReject,
  withdraw: applicationsWithdraw,
  addNote: applicationsAddNote,
};
